import { useEffect, useMemo, useRef, useState } from "react";
import type { Line, Rect, RobotPose, SimEvent } from "../worker/protocol";
import type { SimulatorState } from "../worker/useSimulator";
import robotIconUrl from "../assets/meb-icon-black.png";

// The robot icon's own "forward" edge, as a clockwise angle from screen +x. If the
// icon ever looks rotated the wrong way on the map, change only this — each ±Math.PI/2
// turns it another 90°. (Math.PI/2 = the bottom edge of the image leads.)
const ICON_IMG_FWD = Math.PI / 2;
// Icon size as a fraction of the robot's shoulder width.
const ICON_SCALE = 0.5;
// Follow-camera dead zone: the map pans to keep the robot within this fraction of
// the distance from the map centre to its edge (0.3 = never past 30% out).
const PAN_MARGIN = 0.3;

// Top-down world, centred on world origin (0,0). Base scale shows ~300 cm across
// at zoom 1; zoom multiplies it (clamped MIN..MAX).
const CANVAS_PX = 256;
const BASE_SCALE = CANVAS_PX / 300; // px per cm at zoom 1
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const SONAR_RANGE_CM = 400;
const NO_ECHO_CM = 6553.5; // must match _sim.NO_ECHO_CM (physical no-obstruction value)
// Robot footprint in cm — must match _sim.ROBOT_DEPTH_CM / ROBOT_WIDTH_CM.
const ROBOT_DEPTH_CM = 17; // front-back
const ROBOT_WIDTH_CM = 25; // left-right (shoulders)
const SONAR_OFFSET_CM = ROBOT_DEPTH_CM / 2; // sonar at the front face
// Reading offset (cm) subtracted from the geometric distance, by head angle.
// Piecewise-linear — must match _sim.SONAR_OFFSET_POINTS.
const SONAR_OFFSET_POINTS: [number, number][] = [
  [0, 5],
  [45, 10],
  [90, 15],
  [145, 10],
  [180, 5],
];
function sonarOffsetCm(head: number): number {
  const p = SONAR_OFFSET_POINTS;
  if (head <= p[0][0]) return p[0][1];
  for (let i = 1; i < p.length; i++) {
    const [a0, o0] = p[i - 1];
    const [a1, o1] = p[i];
    if (head <= a1) return o0 + ((o1 - o0) * (head - a0)) / (a1 - a0);
  }
  return p[p.length - 1][1];
}

const COLORS: Record<number, { name: string; css: string }> = {
  1: { name: "BLACK", css: "#111" },
  2: { name: "RED", css: "#e11d48" },
  3: { name: "BLUE", css: "#3b82f6" },
  4: { name: "YELLOW", css: "#eab308" },
  5: { name: "GREEN", css: "#22c55e" },
};

// Body camera model — must match _sim (look-ahead + lateral field, 240 px frame).
const CAMERA_LOOKAHEAD_CM = 20;
const CAMERA_HALF_FIELD_CM = 15;
const CAMERA_FRAME_PX = 240;

function distToSeg(px: number, py: number, l: Line): number {
  const vx = l.x2 - l.x1;
  const vy = l.y2 - l.y1;
  const len2 = vx * vx + vy * vy;
  let t = len2 > 0 ? ((px - l.x1) * vx + (py - l.y1) * vy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (l.x1 + t * vx), py - (l.y1 + t * vy));
}

function segIntersect(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx: number,
  dy: number,
): [number, number] | null {
  const den = (bx - ax) * (dy - cy) - (by - ay) * (dx - cx);
  if (Math.abs(den) < 1e-9) return null;
  const t = ((cx - ax) * (dy - cy) - (cy - ay) * (dx - cx)) / den;
  const u = ((cx - ax) * (by - ay) - (cy - ay) * (bx - ax)) / den;
  if (t >= 0 && t <= 1 && u >= 0 && u <= 1) return [ax + t * (bx - ax), ay + t * (by - ay)];
  return null;
}

// Live camera reading for a colour id: the x-centre in the 240 px frame, or null.
function cameraXCenter(pose: MapPose, lines: Line[], colorId: number): number | null {
  const h = (pose.heading * Math.PI) / 180;
  const fx = Math.cos(h);
  const fy = Math.sin(h);
  const lx = -Math.sin(h);
  const ly = Math.cos(h);
  const px = pose.x + fx * CAMERA_LOOKAHEAD_CM;
  const py = pose.y + fy * CAMERA_LOOKAHEAD_CM;
  const ax = px - lx * CAMERA_HALF_FIELD_CM;
  const ay = py - ly * CAMERA_HALF_FIELD_CM;
  const bx = px + lx * CAMERA_HALF_FIELD_CM;
  const by = py + ly * CAMERA_HALF_FIELD_CM;
  let best: number | null = null;
  for (const ln of lines) {
    if (ln.color !== colorId) continue;
    const it = segIntersect(ax, ay, bx, by, ln.x1, ln.y1, ln.x2, ln.y2);
    if (!it) continue;
    const off = (it[0] - px) * lx + (it[1] - py) * ly;
    if (best === null || Math.abs(off) < Math.abs(best)) best = off;
  }
  if (best === null) return null;
  const half = CAMERA_FRAME_PX / 2;
  return Math.max(0, Math.min(CAMERA_FRAME_PX, half - (best / CAMERA_HALF_FIELD_CM) * half));
}

type MapPose = RobotPose & { head: number; posture: string; blocked: boolean };

function latestPose(events: SimEvent[], fallback: RobotPose): MapPose {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    if (e.dev === "pose") {
      return {
        x: Number(e.args.x),
        y: Number(e.args.y),
        heading: Number(e.args.heading),
        head: e.args.head === undefined ? 90 : Number(e.args.head),
        posture: e.args.posture === undefined ? "stand" : String(e.args.posture),
        blocked: Boolean(e.args.blocked),
      };
    }
  }
  return { ...fallback, head: 90, posture: "stand", blocked: false };
}

// Ray -> axis-aligned box entry distance (mirrors _sim._ray_aabb for the live preview).
function rayAABB(px: number, py: number, dx: number, dy: number, o: Rect): number | null {
  let tmin = 0;
  let tmax = 1e9;
  const slabs: [number, number, number, number][] = [
    [px, dx, o.x, o.x + o.w],
    [py, dy, o.y, o.y + o.h],
  ];
  for (const [p, d, lo, hi] of slabs) {
    if (Math.abs(d) < 1e-9) {
      if (p < lo || p > hi) return null;
    } else {
      let t1 = (lo - p) / d;
      let t2 = (hi - p) / d;
      if (t1 > t2) [t1, t2] = [t2, t1];
      if (t1 > tmin) tmin = t1;
      if (t2 < tmax) tmax = t2;
      if (tmin > tmax) return null;
    }
  }
  return tmin > 0 ? tmin : null;
}

function niceStep(eScale: number): number {
  for (const s of [10, 20, 25, 50, 100, 200, 500]) if (s * eScale >= 44) return s;
  return 1000;
}

export default function SensorPanel({ sim }: { sim: SimulatorState }) {
  const [obstacles, setObstacles] = useState<Rect[]>([]);
  const [robot, setRobot] = useState<RobotPose>({ x: 0, y: 0, heading: 0 });
  const [zoom, setZoom] = useState(1);
  // World point (cm) shown at the canvas centre — the pan offset (follow camera).
  const [view, setView] = useState({ x: 0, y: 0 });
  const [drawMode, setDrawMode] = useState<"wall" | "crossbar" | "line">("wall");
  const [lineColor, setLineColor] = useState(5); // GREEN
  const [lines, setLines] = useState<Line[]>([]);
  const [usMode, setUsMode] = useState<"world" | "manual">("world");
  const [usValue, setUsValue] = useState(30);
  const [imu, setImu] = useState(90);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const dragRef = useRef<{ px: number; py: number } | null>(null);
  // Robot icon drawn on the map, rotated with heading. Loaded once; iconReady
  // flips true on load to trigger a repaint.
  const iconRef = useRef<HTMLImageElement | null>(null);
  const [iconReady, setIconReady] = useState(false);
  // Drag preview in cm: raw start->end (a line, or a rect via min/max).
  const [preview, setPreview] = useState<{ ax: number; ay: number; bx: number; by: number } | null>(
    null,
  );
  // When set, the map shows this pose instead of the live one (used by "reset"
  // so the robot snaps to a spot until the next run replaces it).
  const [poseOverride, setPoseOverride] = useState<MapPose | null>(null);

  const livePose = useMemo(() => latestPose(sim.events, robot), [sim.events, robot]);
  const pose = poseOverride ?? livePose;

  // A new run supersedes any manual override.
  useEffect(() => {
    if (sim.status === "running") setPoseOverride(null);
  }, [sim.status]);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      iconRef.current = img;
      setIconReady(true);
    };
    img.src = robotIconUrl;
  }, []);

  const eScale = BASE_SCALE * zoom;
  const toPx = (x: number, y: number) => ({
    px: CANVAS_PX / 2 + (x - view.x) * eScale,
    py: CANVAS_PX / 2 - (y - view.y) * eScale,
  });
  const toCm = (px: number, py: number) => ({
    x: view.x + (px - CANVAS_PX / 2) / eScale,
    y: view.y + (CANVAS_PX / 2 - py) / eScale,
  });

  // Pan the arena to follow the robot: only shift when it would leave the central
  // dead zone, then just enough to hold it on the 30% boundary. Runs off the
  // primitive pose/scale (not `view`), so it never loops.
  useEffect(() => {
    const maxCm = (PAN_MARGIN * (CANVAS_PX / 2)) / eScale;
    setView((v) => {
      let { x, y } = v;
      if (pose.x - x > maxCm) x = pose.x - maxCm;
      else if (pose.x - x < -maxCm) x = pose.x + maxCm;
      if (pose.y - y > maxCm) y = pose.y - maxCm;
      else if (pose.y - y < -maxCm) y = pose.y + maxCm;
      return x === v.x && y === v.y ? v : { x, y };
    });
  }, [pose.x, pose.y, eScale]);

  // Push world + sensor state to the interpreter whenever it changes.
  useEffect(() => sim.setWorld(obstacles, robot), [obstacles, robot, sim]);
  useEffect(() => sim.setUltrasonic(usMode === "manual" ? usValue : null), [usMode, usValue, sim]);
  useEffect(() => sim.setImu(imu), [imu, sim]);
  useEffect(() => sim.setLines(lines), [lines, sim]);

  // Live camera reading: which drawn line (if any) the body camera sees, and its
  // x-centre in the 240 px frame — mirrors _sim so the panel reacts live.
  const camera = useMemo(() => {
    for (const c of [lineColor, 1, 2, 3, 4, 5]) {
      const xc = cameraXCenter(pose, lines, c);
      if (xc !== null) return { color: c, x: Math.round(xc) };
    }
    return null;
  }, [pose, lines, lineColor]);

  // Live sonar reading (manual value, or a JS ray-cast mirroring the VM) so the
  // panel reacts immediately as the robot / obstacles / manual value change.
  const sonar = useMemo(() => {
    // cm = reported value; rayCm = geometric distance the beam actually travels
    // (so the drawn ray reaches the wall even though the value reads 10 cm short).
    if (usMode === "manual") {
      const echo = usValue < NO_ECHO_CM;
      return { cm: usValue, echo, rayCm: echo ? usValue : SONAR_RANGE_CM };
    }
    const a = ((pose.heading + (pose.head - 90)) * Math.PI) / 180;
    const dx = Math.cos(a);
    const dy = Math.sin(a);
    // Cast from the front face of the body, matching _sim.
    const hb = (pose.heading * Math.PI) / 180;
    const ox = pose.x + Math.cos(hb) * SONAR_OFFSET_CM;
    const oy = pose.y + Math.sin(hb) * SONAR_OFFSET_CM;
    let best: number | null = null;
    for (const o of obstacles) {
      const active = (o.kind ?? 0) === 0 || pose.posture === "stand";
      if (!active) continue;
      const d = rayAABB(ox, oy, dx, dy, o);
      if (d !== null && (best === null || d < best)) best = d;
    }
    if (best === null || best > SONAR_RANGE_CM)
      return { cm: NO_ECHO_CM, echo: false, rayCm: SONAR_RANGE_CM };
    const reported = Math.max(0, Math.round(best) - Math.round(sonarOffsetCm(pose.head)));
    return { cm: reported, echo: true, rayCm: best };
  }, [usMode, usValue, obstacles, pose]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, CANVAS_PX, CANVAS_PX);
    ctx.fillStyle = "#6b7280"; // darker grey arena (still light enough for black lines)
    ctx.fillRect(0, 0, CANVAS_PX, CANVAS_PX);

    // ruler grid + cm labels
    const step = niceStep(eScale);
    ctx.font = "9px monospace";
    const xr = [toCm(0, 0).x, toCm(CANVAS_PX, 0).x];
    const yr = [toCm(0, CANVAS_PX).y, toCm(0, 0).y];
    ctx.strokeStyle = "#9ca3af"; // grid
    ctx.fillStyle = "#e5e7eb"; // ruler labels (light, for the darker background)
    ctx.lineWidth = 1;
    for (let gx = Math.ceil(xr[0] / step) * step; gx <= xr[1]; gx += step) {
      const { px } = toPx(gx, 0);
      ctx.beginPath();
      ctx.moveTo(px, 0);
      ctx.lineTo(px, CANVAS_PX);
      ctx.stroke();
      if (gx !== 0) ctx.fillText(String(gx), px + 2, CANVAS_PX - 2);
    }
    for (let gy = Math.ceil(yr[0] / step) * step; gy <= yr[1]; gy += step) {
      const { py } = toPx(0, gy);
      ctx.beginPath();
      ctx.moveTo(0, py);
      ctx.lineTo(CANVAS_PX, py);
      ctx.stroke();
      if (gy !== 0) ctx.fillText(String(gy), 2, py - 2);
    }
    // origin axes
    ctx.strokeStyle = "#cbd5e1";
    const o0 = toPx(0, 0);
    ctx.beginPath();
    ctx.moveTo(o0.px, 0);
    ctx.lineTo(o0.px, CANVAS_PX);
    ctx.moveTo(0, o0.py);
    ctx.lineTo(CANVAS_PX, o0.py);
    ctx.stroke();

    // obstacles: walls solid grey, crossbars dashed orange (head-level only)
    for (const ob of obstacles) {
      const tl = toPx(ob.x, ob.y + ob.h);
      const w = ob.w * eScale;
      const h = ob.h * eScale;
      if ((ob.kind ?? 0) === 1) {
        ctx.fillStyle = "rgba(249,115,22,0.35)";
        ctx.fillRect(tl.px, tl.py, w, h);
        ctx.strokeStyle = "#f97316";
        ctx.setLineDash([4, 3]);
        ctx.strokeRect(tl.px, tl.py, w, h);
        ctx.setLineDash([]);
      } else {
        ctx.fillStyle = "#1e293b"; // dark walls, distinct from the grey arena
        ctx.fillRect(tl.px, tl.py, w, h);
      }
    }
    // coloured lines the camera follows (2 cm wide, min 2 px)
    ctx.lineCap = "round";
    for (const ln of lines) {
      const a = toPx(ln.x1, ln.y1);
      const b = toPx(ln.x2, ln.y2);
      ctx.strokeStyle = COLORS[ln.color]?.css ?? "#fff";
      ctx.lineWidth = Math.max(2, 2 * eScale);
      ctx.beginPath();
      ctx.moveTo(a.px, a.py);
      ctx.lineTo(b.px, b.py);
      ctx.stroke();
    }
    ctx.lineCap = "butt";
    ctx.lineWidth = 1;

    if (preview) {
      if (drawMode === "line") {
        const a = toPx(preview.ax, preview.ay);
        const b = toPx(preview.bx, preview.by);
        ctx.strokeStyle = COLORS[lineColor]?.css ?? "#fff";
        ctx.lineWidth = Math.max(2, 2 * eScale);
        ctx.beginPath();
        ctx.moveTo(a.px, a.py);
        ctx.lineTo(b.px, b.py);
        ctx.stroke();
        ctx.lineWidth = 1;
      } else {
        const x = Math.min(preview.ax, preview.bx);
        const yTop = Math.max(preview.ay, preview.by);
        const w = Math.abs(preview.ax - preview.bx);
        const hgt = Math.abs(preview.ay - preview.by);
        const tl = toPx(x, yTop);
        ctx.strokeStyle = drawMode === "crossbar" ? "#f97316" : "#38bdf8";
        ctx.strokeRect(tl.px, tl.py, w * eScale, hgt * eScale);
      }
    }

    // Robot: rectangular BODY (top-down) oriented by heading, with a circular
    // HEAD at the front. The head-mounted sonar ray shows head direction, which
    // may differ from the body heading.
    const r = toPx(pose.x, pose.y);
    const h = (pose.heading * Math.PI) / 180;
    const squat = pose.posture === "squat";
    const fx = Math.cos(h);
    const fy = -Math.sin(h); // forward (canvas coords)
    const lx = -fy;
    const ly = fx; // left-perpendicular (shoulder axis)
    // Drawn to real scale (px = cm × zoom scale), with a small floor for
    // visibility at low zoom. DEP = front-back, WID = shoulders.
    const DEP = Math.max(2, ROBOT_DEPTH_CM * eScale);
    const WID = Math.max(4, ROBOT_WIDTH_CM * eScale);
    const corner = (fwd: number, side: number): [number, number] => [
      r.px + fx * fwd + lx * side,
      r.py + fy * fwd + ly * side,
    ];
    const pts = [
      corner(DEP / 2, WID / 2),
      corner(DEP / 2, -WID / 2),
      corner(-DEP / 2, -WID / 2),
      corner(-DEP / 2, WID / 2),
    ];
    // Robot body = a WHITE footprint plate (the true collision rectangle) with a
    // state-coloured outline (green normal, red blocked, amber squat). The robot
    // icon sits on top, anchored to the centre and rotated to the heading.
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < 4; i++) ctx.lineTo(pts[i][0], pts[i][1]);
    ctx.closePath();
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.strokeStyle = pose.blocked ? "#dc2626" : squat ? "#d97706" : "#059669";
    ctx.lineWidth = squat ? 2 : 1.5;
    ctx.stroke();
    const icon = iconRef.current;
    if (icon) {
      // Preserve the icon's native aspect ratio (no left/right stretch): span the
      // shoulder width, derive height from the image's own proportions.
      const iw = icon.naturalWidth || 1;
      const ih = icon.naturalHeight || 1;
      const dw = WID * ICON_SCALE;
      const dh = (ih / iw) * WID * ICON_SCALE;
      ctx.save();
      ctx.translate(r.px, r.py);
      ctx.rotate(Math.atan2(fy, fx) - ICON_IMG_FWD);
      ctx.drawImage(icon, -dw / 2, -dh / 2, dw, dh);
      ctx.restore();
    }

    // head (circle) at the front; sonar ray from the front face in the head
    // direction. Ray length uses the geometric distance so it reaches the wall.
    const headX = r.px + fx * (DEP / 2);
    const headY = r.py + fy * (DEP / 2);
    const sonarX = r.px + fx * (SONAR_OFFSET_CM * eScale);
    const sonarY = r.py + fy * (SONAR_OFFSET_CM * eScale);
    const sonarA = ((pose.heading + (pose.head - 90)) * Math.PI) / 180;
    const rayLen = sonar.rayCm * eScale;
    ctx.strokeStyle = sonar.echo ? "rgba(56,189,248,0.9)" : "rgba(56,189,248,0.3)";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(sonarX, sonarY);
    ctx.lineTo(sonarX + Math.cos(sonarA) * rayLen, sonarY - Math.sin(sonarA) * rayLen);
    ctx.stroke();
    ctx.fillStyle = "#0891b2"; // head (darker cyan for the light background)
    ctx.beginPath();
    ctx.arc(headX, headY, 4, 0, Math.PI * 2);
    ctx.fill();

    // camera view: dashed strip the body camera samples (look-ahead + lateral field)
    const camX = r.px + fx * (CAMERA_LOOKAHEAD_CM * eScale);
    const camY = r.py + fy * (CAMERA_LOOKAHEAD_CM * eScale);
    const camHalf = CAMERA_HALF_FIELD_CM * eScale;
    ctx.strokeStyle = "rgba(244,244,245,0.85)";
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 2]);
    ctx.beginPath();
    ctx.moveTo(camX + lx * camHalf, camY + ly * camHalf);
    ctx.lineTo(camX - lx * camHalf, camY - ly * camHalf);
    ctx.stroke();
    ctx.setLineDash([]);

    if (pose.blocked) {
      ctx.fillStyle = "#b91c1c";
      ctx.font = "bold 10px monospace";
      ctx.fillText("⛔ stalled", r.px + 12, r.py - 10);
    } else if (squat) {
      ctx.fillStyle = "#b45309";
      ctx.font = "bold 10px monospace";
      ctx.fillText("squat", r.px + 12, r.py - 10);
    }
  }, [obstacles, lines, preview, pose, eScale, zoom, drawMode, lineColor, sonar, iconReady, view]);

  function evtToPx(e: React.MouseEvent) {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { px: e.clientX - rect.left, py: e.clientY - rect.top };
  }
  function onDown(e: React.MouseEvent) {
    dragRef.current = evtToPx(e);
  }
  function onMove(e: React.MouseEvent) {
    if (!dragRef.current) return;
    const a = toCm(dragRef.current.px, dragRef.current.py);
    const p = evtToPx(e);
    const b = toCm(p.px, p.py);
    setPreview({ ax: a.x, ay: a.y, bx: b.x, by: b.y });
  }
  function onUp(e: React.MouseEvent) {
    const start = dragRef.current;
    dragRef.current = null;
    setPreview(null);
    if (!start) return;
    const p = evtToPx(e);
    if (Math.hypot(p.px - start.px, p.py - start.py) < 5) {
      // click: remove an obstacle or a line under the point
      const c = toCm(p.px, p.py);
      setObstacles((prev) =>
        prev.filter((o) => !(c.x >= o.x && c.x <= o.x + o.w && c.y >= o.y && c.y <= o.y + o.h)),
      );
      setLines((prev) => prev.filter((l) => distToSeg(c.x, c.y, l) > 4));
      return;
    }
    const a = toCm(start.px, start.py);
    const b = toCm(p.px, p.py);
    if (drawMode === "line") {
      setLines((prev) => [...prev, { x1: a.x, y1: a.y, x2: b.x, y2: b.y, color: lineColor }]);
    } else {
      setObstacles((prev) => [
        ...prev,
        {
          x: Math.min(a.x, b.x),
          y: Math.min(a.y, b.y),
          w: Math.abs(a.x - b.x),
          h: Math.abs(a.y - b.y),
          kind: drawMode === "crossbar" ? 1 : 0,
        },
      ]);
    }
  }

  const clampZoom = (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
  const label = "block text-[11px] font-medium text-slate-400";
  const numIn = "w-14 rounded bg-slate-900 px-1 py-0.5 text-xs text-slate-200";
  const chip = (active: boolean) =>
    `rounded px-2 py-0.5 text-xs ${active ? "bg-sky-600 text-white" : "bg-slate-700 text-slate-200 hover:bg-slate-600"}`;

  return (
    <div className="space-y-4 text-sm text-slate-300">
      <div>
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-semibold text-slate-200">Arena map</h2>
          <div className="flex items-center gap-1 text-xs">
            <button
              type="button"
              className={chip(false)}
              onClick={() => setZoom((z) => clampZoom(z / 1.25))}
            >
              −
            </button>
            <span className="w-9 text-center text-slate-400">{Math.round(zoom * 100)}%</span>
            <button
              type="button"
              className={chip(false)}
              onClick={() => setZoom((z) => clampZoom(z * 1.25))}
            >
              +
            </button>
          </div>
        </div>
        <p className="mb-1 text-[11px] text-slate-500">
          Drag to add · click to remove. Green rect = body, cyan = head, blue = sonar, dashed =
          camera view.
        </p>
        <div className="mb-1 flex flex-wrap items-center gap-1 text-xs">
          <span className="text-slate-500">Draw:</span>
          <button
            type="button"
            className={chip(drawMode === "wall")}
            onClick={() => setDrawMode("wall")}
          >
            Wall
          </button>
          <button
            type="button"
            className={chip(drawMode === "crossbar")}
            onClick={() => setDrawMode("crossbar")}
          >
            Crossbar
          </button>
          <button
            type="button"
            className={chip(drawMode === "line")}
            onClick={() => setDrawMode("line")}
          >
            Line
          </button>
          {drawMode === "line" &&
            Object.entries(COLORS).map(([idStr, c]) => {
              const id = Number(idStr);
              return (
                <button
                  key={id}
                  type="button"
                  title={c.name}
                  onClick={() => setLineColor(id)}
                  className={`h-4 w-4 rounded ${lineColor === id ? "ring-2 ring-white" : ""}`}
                  style={{ background: c.css }}
                />
              );
            })}
        </div>
        <canvas
          ref={canvasRef}
          width={CANVAS_PX}
          height={CANVAS_PX}
          className="cursor-crosshair rounded border border-slate-700"
          onMouseDown={onDown}
          onMouseMove={onMove}
          onMouseUp={onUp}
          onMouseLeave={() => {
            dragRef.current = null;
            setPreview(null);
          }}
        />
        <div className="mt-1 flex items-center justify-between text-[11px]">
          <span className="text-slate-500">
            robot ({pose.x.toFixed(0)},{pose.y.toFixed(0)}) hd {pose.heading.toFixed(0)}° ·{" "}
            {pose.posture} · sonar{" "}
            <span className="text-sky-300">{sonar.echo ? `${sonar.cm} cm` : "no echo"}</span>
            {pose.blocked && <span className="text-red-400"> · ⛔ stalled</span>}
          </span>
          <button
            type="button"
            className="rounded bg-slate-700 px-2 py-0.5 hover:bg-slate-600"
            onClick={() => {
              setObstacles([]);
              setLines([]);
            }}
          >
            Clear
          </button>
        </div>
      </div>

      <div>
        <span className={label}>Robot start (cm, cm, °)</span>
        <div className="mt-1 flex items-center gap-1">
          {(["x", "y", "heading"] as const).map((k) => (
            <input
              key={k}
              type="number"
              className={numIn}
              value={robot[k]}
              onChange={(e) => setRobot((r) => ({ ...r, [k]: Number(e.target.value) }))}
              aria-label={`robot ${k}`}
            />
          ))}
          <button
            type="button"
            className="rounded bg-slate-700 px-2 py-0.5 text-xs hover:bg-slate-600"
            onClick={() => {
              setRobot({ x: 0, y: 0, heading: 0 });
              setPoseOverride({
                x: 0,
                y: 0,
                heading: 0,
                head: 90,
                posture: "stand",
                blocked: false,
              });
              setView({ x: 0, y: 0 });
            }}
            title="Move the robot back to the origin (0, 0)"
          >
            Reset to (0,0)
          </button>
        </div>
      </div>

      <div>
        <span className={label}>Ultrasonic</span>
        {/* What getDistance() would return right now with the current settings. */}
        <div className="mt-1 rounded bg-slate-900 px-2 py-1 text-xs">
          <span className="text-slate-400">Sonar reads now: </span>
          <span className="font-semibold text-sky-300">
            {sonar.echo ? `${sonar.cm} cm` : "6553.5 cm (no echo)"}
          </span>
        </div>
        <div className="mt-1 flex gap-2 text-xs">
          <label className="flex items-center gap-1">
            <input type="radio" checked={usMode === "world"} onChange={() => setUsMode("world")} />
            Arena (ray-cast)
          </label>
          <label className="flex items-center gap-1">
            <input
              type="radio"
              checked={usMode === "manual"}
              onChange={() => setUsMode("manual")}
            />
            Manual
          </label>
        </div>
        {usMode === "manual" && (
          <div className="mt-1 flex items-center gap-2">
            <input
              type="number"
              className={numIn}
              value={usValue}
              onChange={(e) => setUsValue(Number(e.target.value))}
              aria-label="ultrasonic cm"
            />
            <span className="text-xs text-slate-500">cm</span>
            <button
              type="button"
              className="rounded bg-slate-700 px-2 py-0.5 text-xs hover:bg-slate-600"
              onClick={() => setUsValue(NO_ECHO_CM)}
            >
              No echo (6553.5)
            </button>
          </div>
        )}
      </div>

      <div>
        <span className={label}>IMU X-tilt (fall detection)</span>
        <div className="mt-1 flex flex-wrap gap-1 text-xs">
          <button type="button" className={chip(false)} onClick={() => setImu(90)}>
            Upright
          </button>
          <button type="button" className={chip(false)} onClick={() => setImu(30)}>
            Fallen fwd
          </button>
          <button type="button" className={chip(false)} onClick={() => setImu(140)}>
            Fallen back
          </button>
          <input
            type="number"
            className={numIn}
            value={imu}
            onChange={(e) => setImu(Number(e.target.value))}
            aria-label="imu x angle"
          />
        </div>
      </div>

      <div>
        <span className={label}>Camera (follows a drawn line)</span>
        <p className="mt-1 text-[11px] text-slate-500">
          Draw a coloured line on the map (above). The body camera looks {CAMERA_LOOKAHEAD_CM} cm
          ahead across a ±{CAMERA_HALF_FIELD_CM} cm strip (dashed line on the map) and reports the
          line's x in a {CAMERA_FRAME_PX} px frame — 120 = centred.
        </p>
        <div className="mt-1 rounded bg-slate-900 px-2 py-1 text-xs">
          <span className="text-slate-400">Camera sees: </span>
          {camera ? (
            <span className="font-semibold" style={{ color: COLORS[camera.color]?.css }}>
              {COLORS[camera.color]?.name} line · x = {camera.x}
              <span className="text-slate-400">
                {" "}
                ({camera.x < 110 ? "left" : camera.x > 130 ? "right" : "centred"})
              </span>
            </span>
          ) : (
            <span className="text-slate-500">no line</span>
          )}
        </div>
      </div>
    </div>
  );
}
